// @ts-strict-ignore
import { RootState } from './reducers/reducers';
import {
    AppMonitoringHealthMetric,
    AppMonitoringHealthMetricCategory,
    AppMonitoringHealthMetricType,
    AppPlatform,
    AppStoreArtifactUploadDestinationType,
    AppStoreType,
    AutomationType,
    BuildDistroBucketRuleType,
    ExtendedStatisticType,
    IntegrationCapability,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationType,
    MetricConditionOperator,
    MonitoringAutomationConditionType,
    MonitoringMetricHealthStatus,
    MonitoringStatistic,
    ObservabilityAndAnalyticsMetricType,
    ReleaseStepType,
    SettingType,
    StabilityMetricType,
    Statistic,
    StatisticTimeSeriesDataPoint,
    UserRatingsMetricType,
    VCSUserData,
    WorkflowType,
} from './types';
import Icon, { FieldBinaryOutlined } from '@ant-design/icons';
import { Location } from 'history';
import { Rule } from 'antd/lib/form';
import { InputNumberProps } from 'antd/lib/input-number';
import { ComponentType } from 'react';
import { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';

export interface StringKeyedDictionary<T> {
    [key: string]: T;
}

export interface NumberKeyedDictionary<T> {
    [key: number]: T;
}

export function allCases<T>(enumeration: T): Array<T[keyof T]> {
    const keys = Object.keys(enumeration).map((k) => enumeration[k]);

    const items = new Array<T[keyof T]>();
    for (const elem of keys) {
        items.push(elem);
    }

    return items;
}

// Expected type for React Router `match` params when parsing an App Route

export interface AppRouteComponentMatchParams {
    appIdParam: string;
    orgIdParam: string;
}

// Expected type for React Router `match` params when parsing an Org Route

export interface OrgRouteComponentMatchParams {
    orgIdParam: string;
    orgSettingsTabType?: string;
}

// Expected type for React Router `match` params when parsing an Release Route

export interface ReleaseRouteComponentMatchParams extends AppRouteComponentMatchParams {
    releaseIdParam: string;
}

export interface ReleaseRolloutRouteComponentMatchParams extends ReleaseRouteComponentMatchParams {
    tabTypeParam: string;
}

/// Expected type for React Router `match` params when parsing a Release Step Route

export interface ReleaseStepRouteComponentMatchParams extends ReleaseRouteComponentMatchParams {
    releaseStepTypeParam: string;
}

export interface StepRunRouteComponentMatchParams extends ReleaseRouteComponentMatchParams {
    stepRunIdParam: string;
}

export interface StepRunsSummaryRouteComponentMatchParams extends ReleaseRouteComponentMatchParams {
    groupIndexParam: string;
    archetypeGroupIndexParam: string;
}

export interface ReleaseStepTabRouteComponentMatchParams extends ReleaseStepRouteComponentMatchParams {
    releaseStepTabType: string;
}

export interface SettingsTypeComponentMatchParams extends AppRouteComponentMatchParams {
    settingType: SettingType;
}

/// Expected type for React Router `match` params when parsing the Integrations route

export interface BucketRouteComponentMatchParams extends AppRouteComponentMatchParams {
    bucketIdParam: string;
}

export interface IntegrationsRouteComponentMatchParams extends AppRouteComponentMatchParams {
    integrationTypeParam: string;
}

export interface OrgSettingsTypeComponentMatchParams extends OrgRouteComponentMatchParams {
    orgSettingsTabType: OrgSettingsTabType;
    /// Expected type for React Router `match` params when parsing the Setup route
}

export interface SetupRouteComponentMatchParams extends AppRouteComponentMatchParams {
    setupTypeParam: string;
}

export interface AutomationsRouteComponentMatchParams extends AppRouteComponentMatchParams {
    automationsTypeParam: string;
}

/// Type for location.state set to capture source Route location during a redirect

export interface RedirectState {
    fromLocation?: Location;
}

export interface OrgComponentProps {
    orgId: string;
    isOrgSettingsRoute?: boolean;
}

export interface ProviderComponentProps extends OrgComponentProps {
    provider: IntegrationProvider;
}

export interface AppComponentProps extends OrgComponentProps {
    appId: string;
}

export interface BucketComponentProps extends AppComponentProps {
    bucketId: string;
}

export interface IntegrationRouteProps extends AppComponentProps {
    integrationType?: IntegrationType;
}

export interface ReleaseComponentProps extends AppComponentProps {
    releaseId: string;
}

export interface ReleaseStepComponentProps extends ReleaseComponentProps {
    releaseStepType: Nullable<ReleaseStepType>;
}

// TODO: #WORKFLOWS# calling this temporal because this contains some unmigrated params like (appId, releaseId) and we will progressively migrate them to workflowId, workflowRunId.
export interface TemporalStepRunComponentProps extends ReleaseComponentProps {
    stepRunId: string;
}

export interface WorkflowComponentProps extends OrgComponentProps {
    workflowId: string;
}

export interface WorkflowRunComponentProps extends WorkflowComponentProps {
    workflowRunId: string;
}

export interface StepConfigComponentProps extends WorkflowRunComponentProps, ReleaseComponentProps {
    stepConfigId: string;
}

export interface StepRunComponentProps extends StepConfigComponentProps {
    stepRunId: string;
}

export interface IconProps {
    width: number;
    height: number;
}

export enum OrgSettingsTabType {
    Team = 'team',
    Checklists = 'checklists',
    Webhooks = 'webhooks',
    Api = 'api',
    SsoSaml = 'sso-saml',
    Tags = 'tags',
    AI = 'ai',
    Integration = 'integration',
    BuildDistro = 'build-distro',
    Triage = 'triage',
}

export enum AIFeatureArea {
    ReleaseNotes = 'releaseNotes',
    AppStoreReviews = 'appStoreReviews',
    Chatbot = 'chatbot',
    OrgInsights = 'orgInsights',
}

export enum AIProviderType {
    OpenAI = 'openai',
    Claude = 'claude',
}

export const AllAIProviderTypes = allCases(AIProviderType);

export enum SetupStepType {
    DefaultReleaseType = 'default-release-type',
    AppStore = 'app-store',
    BranchingStrategy = 'branching-strategy',
    ReleaseTags = 'release-tags',
    FeatureAffiliations = 'feature-affiliations',
    FreeformFeedback = 'feedback',
    Automations = 'automations',
    AppNotifications = 'app-notifications',
    KeyIntegrations = 'key-integrations',
}

export interface IntegrationsRecommendationBannerData {
    type: SetupStepType;
    title: (commsProviders: string) => string;
    description: string;
    actionText: string;
    integrationCapabilities: IntegrationCapability[];
}

export interface CommonSetupViewProps extends AppComponentProps {
    onChange: (hasUnsavedChanges: boolean) => void;
}

export interface SetupViewProps extends CommonSetupViewProps {
    displayRelatedIntegrationsSetup: () => void;
    context?: SetupStepsCallerContext;
}

export interface QuickStartCISetupViewProps extends OrgComponentProps {
    appId?: string;
}

export interface CommonSetupStep<T> {
    title: string | ((platform: AppPlatform, appStoreType: AppStoreType) => string);
    description: (commsProviders: string) => string;
    icon: (platform: AppPlatform) => typeof Icon | ComponentType<CustomIconComponentProps>;
    SetupViewComponent: React.FC<T>;
    InfoViewComponent: React.FC<Partial<AppComponentProps>>;
    handleOnNextStep?: () => void;
    isCompleteSelector: (root: RootState, appComponentProps: AppComponentProps) => boolean;
}

export interface SetupStep extends CommonSetupStep<SetupViewProps> {
    listTitle: string;
    listDescription: string;
    integrationsRecommendationBannerData: IntegrationsRecommendationBannerData;
}

export interface QuickStartCISetupStep
    extends Omit<CommonSetupStep<QuickStartCISetupViewProps>, 'title' | 'description'> {
    headerTitle: string | ((platform: AppPlatform, appStoreType: AppStoreType) => string);
    isSkippable?: boolean;
}

export enum QuickStartCISetupStepType {
    WhatIsQuickStartCI = 'what-is-quick-start-ci',
    PlatformConfirmation = 'platform-confirmation',
    VCSIntegration = 'vcs-integration',
    VCSSettings = 'vcs-settings',
    AppStoreIntegration = 'app-store-integration',
    ScanDetails = 'scan-details',
    ScanDetailsConfirmation = 'scan-details-confirmation',
    UploadSecrets = 'upload-secrets',
    DownloadFiles = 'download-files',
}

export interface UserAppData {
    appId: string;
    appName: string;
    platform: AppPlatform;
    appStoreType?: AppStoreType;
}

export const symbolForMetricConditionOperator = (operator: MetricConditionOperator) => {
    switch (operator) {
        case MetricConditionOperator.GreaterThan:
            return '>';
        case MetricConditionOperator.GreaterThanOrEqualTo:
            return '>=';
        case MetricConditionOperator.LessThan:
            return '<';
        case MetricConditionOperator.LessThanOrEqualTo:
            return '<=';
        default:
            return null;
    }
};

export const negatedOperator = (operator: MetricConditionOperator) => {
    switch (operator) {
        case MetricConditionOperator.GreaterThan:
            return MetricConditionOperator.LessThanOrEqualTo;
        case MetricConditionOperator.GreaterThanOrEqualTo:
            return MetricConditionOperator.LessThan;
        case MetricConditionOperator.LessThan:
            return MetricConditionOperator.GreaterThanOrEqualTo;
        case MetricConditionOperator.LessThanOrEqualTo:
            return MetricConditionOperator.GreaterThan;
        default:
            return null;
    }
};

export const displayNameForMetricConditionOperator = (operator: MetricConditionOperator) => {
    switch (operator) {
        case MetricConditionOperator.GreaterThan:
            return 'Greater than';
        case MetricConditionOperator.GreaterThanOrEqualTo:
            return 'Greater than or equal to';
        case MetricConditionOperator.LessThan:
            return 'Less than';
        case MetricConditionOperator.LessThanOrEqualTo:
            return 'Less than or equal to';
        default:
            return '';
    }
};

export enum DeltaMetricOperator {
    Above = 'above',
    Below = 'below',
}

export const displayNameForDeltaMetricOperator = (operator: DeltaMetricOperator) => {
    switch (operator) {
        case DeltaMetricOperator.Above:
            return 'above';
        case DeltaMetricOperator.Below:
            return 'below';
    }
};

export interface AppMonitoringMetricData extends AppMonitoringHealthMetric {
    title: string;
    formattedThreshold: string;
    isUsedForAutoAccelerate: boolean;
    isUsedForAutoHalt: boolean;
    canBeRemoved: boolean;
    integrationCapability: IntegrationCapability;
    currentAverage?: string;
    integrationIconAlt?: string;
    integrationIcon?: string;
}

export const enum MonitoringSettingsStaticIds {
    UserStabilityRate = 'user-stability-rate',
    SessionStabilityRate = 'session-stability-rate',
    Ratings = 'ratings',
}

export interface MonitoringConditionOption {
    id: MonitoringAutomationConditionType;
    title: string;
}

export type MetricsUsageOnAutomationsMap = Record<
    MonitoringSettingsStaticIds | string,
    {
        isUsedForAutoAccelerate: boolean;
        isUsedForAutoHalt: boolean;
    }
>;

export type ExtendedStatsByType = Partial<Record<ExtendedStatisticType, Statistic>>;

// Monitoring

export interface GroupedMonitoringStatistic {
    key: string;
    category: AppMonitoringHealthMetricCategory;
    mergedStatus?: MonitoringMetricHealthStatus;
    title: string;
    groups: MonitoringStatisticGroup[];
    integrationType?: IntegrationType;
    buildIdentifier?: string;
    description?: string;
}

export interface MonitoringStatisticAutomationUsage {
    isUsed: boolean;
    triggeredAt?: string;
}

export interface MonitoringStatisticGroup {
    value: string;
    unit: string;
    type: AppMonitoringHealthMetricType;
    dataPoints: StatisticTimeSeriesDataPoint[];
    additionalDataSeries?: Record<string, StatisticTimeSeriesDataPoint[]>;
    hasRecentDataOnly: boolean;
    threshold?: number;
    isMoreBetter?: boolean;
    status?: MonitoringMetricHealthStatus;
    currentValue?: number;
    delta?: string;
    change?: number;
    previousVersion?: string;
    previousValue?: string;
    automationUsage: Record<
        AutomationType.AutoHaltUnstablePhasedReleases | AutomationType.AutoPromoteStablePhasedReleases,
        MonitoringStatisticAutomationUsage
    >;
}

export const displayMonitoringStatisticTitle = (monitoringStatistic: MonitoringStatistic) => {
    switch (monitoringStatistic.type) {
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue: {
            let title = monitoringStatistic.eventName ?? 'Unknown event';
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                title = `${title} (${monitoringStatistic.paramName})`;
            }
            return title;
        }
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
            return `${monitoringStatistic.eventName ?? 'Unknown event'} (${
                monitoringStatistic.paramName ?? 'Unknown parameter'
            })`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue: {
            const eventAndParamString = `${monitoringStatistic.eventName ?? 'Unknown event'} (${
                monitoringStatistic.paramName ?? 'Unknown parameter'
            })`;
            return `${eventAndParamString} (delta)`;
        }
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage: {
            const eventAndParamString = `${monitoringStatistic.eventName ?? 'Unknown event'} (${
                monitoringStatistic.paramName ?? 'Unknown parameter'
            })`;
            return `${eventAndParamString} (delta as %)`;
        }
        case StabilityMetricType.SessionStability:
            return 'Crash-free rate';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
            return 'Crash-free rate (delta)';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
            return 'Crash-free rate (delta as %)';
        case StabilityMetricType.UserStability:
            return 'Crash-free rate';
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
            return 'Crash-free rate (delta)';
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return 'Crash-free rate (delta as %)';
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.MergedUserStability:
            return 'Crash-free rate';
        case StabilityMetricType.AdoptionRate:
            return 'Adoption rate';
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return 'User ratings';
    }
};

export const displayUngroupedMonitoringStatisticTitle = (monitoringStatistic: MonitoringStatistic) => {
    const eventName = monitoringStatistic.eventName ?? 'Unknown event';
    const paramName = monitoringStatistic.paramName ?? 'Unknown parameter';
    switch (monitoringStatistic.type) {
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per DAU`;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per DAU (delta as %)`;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per session (delta as %)`;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerSession: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per session`;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per DAU (delta)`;
        }
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue: {
            let str = eventName;
            if (monitoringStatistic.paramName) {
                // possible if filtered param is present
                str = `${str} (${monitoringStatistic.paramName})`;
            }
            return `${str} per session (delta)`;
        }
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
            return `${eventName} (${paramName}) (avg delta as %)`;
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
            return `${eventName} (${paramName}) (median delta as %)`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
            return `${eventName} (${paramName}) (90p delta as %)`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
            return `${eventName} (${paramName}) (95p delta as %)`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
            return `${eventName} (${paramName}) (99p delta as %)`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
            return `${eventName} (${paramName}) (avg delta)`;
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return `${eventName} (${paramName}) (median delta)`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
            return `${eventName} (${paramName}) (90p delta)`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
            return `${eventName} (${paramName}) (95p delta)`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return `${eventName} (${paramName}) (99p delta)`;
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
            return `${eventName} (${paramName}) (avg)`;
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
            return `${eventName} (${paramName}) (median)`;
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
            return `${eventName} (${paramName}) (90p)`;
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
            return `${eventName} (${paramName}) (95p)`;
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
            return `${eventName} (${paramName}) (99p)`;
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
            return eventName;
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
            return `${eventName} (delta as %)`;
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
            return `${eventName} (delta)`;
        case ObservabilityAndAnalyticsMetricType.Composed:
            return eventName;
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
            return `${eventName} (delta as %)`;
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return `${eventName} (delta)`;
        case StabilityMetricType.SessionStability:
            return 'Crash-free sessions';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
            return 'Crash-free sessions (delta as %)';
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
            return 'Crash-free sessions (delta)';
        case StabilityMetricType.UserStability:
            return 'Crash-free users';
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
            return 'Crash-free users (delta as %)';
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return 'Crash-free users (delta)';
        case UserRatingsMetricType.AvgUserRating:
            return 'User ratings';
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
            return 'User ratings (delta as %)';
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return 'User ratings (delta)';
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.MergedUserStability:
            return 'Crash-free rate';
        case StabilityMetricType.AdoptionRate:
            return 'Adoption rate';
    }
};

export const categoryForMonitoringStatisticType = (type: AppMonitoringHealthMetricType) => {
    switch (type) {
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return AppMonitoringHealthMetricCategory.ObservabilityAndAnalytics;
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
        case StabilityMetricType.UserStability:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.MergedUserStability:
        case StabilityMetricType.AdoptionRate:
            return AppMonitoringHealthMetricCategory.Stability;
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return AppMonitoringHealthMetricCategory.UserReviews;
    }
};

export const stabilityMetricGroupKey = (
    type: StabilityMetricType,
    installationId: IntegrationInstallationId | undefined,
) => `${type}::${installationId ?? ''}`;

export const groupKeyByMonitoringStatistic = (monitoringStatistic: MonitoringStatistic) => {
    switch (monitoringStatistic.type) {
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'average-and-median-property-values',
            );
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'average-and-median-property-values-delta-percent',
            );
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'average-and-median-property-values-delta-value',
            );
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-session-and-user',
            );
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-user-delta-percent',
            );
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-user-delta-value',
            );
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-session-delta-percent',
            );
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-session-delta-value',
            );
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-p90-p95-p99',
            );
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-p90-p95-p99-delta-percent',
            );
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.paramName,
                monitoringStatistic.integrationId,
                'events-p90-p95-p99-delta-value',
            );
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.integrationId,
                'events-custom',
            );
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return obaaMonitoringStatGroupKey(
                monitoringStatistic.eventName,
                monitoringStatistic.integrationId,
                'events-composed',
            );
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
            return stabilityMetricGroupKey(
                StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
            return stabilityMetricGroupKey(
                StabilityMetricType.SessionStabilityDeltaFromPreviousValue,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.SessionStability:
            return stabilityMetricGroupKey(
                StabilityMetricType.SessionStability,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
            return stabilityMetricGroupKey(
                StabilityMetricType.UserStabilityDeltaFromPreviousPercentage,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return stabilityMetricGroupKey(
                StabilityMetricType.UserStabilityDeltaFromPreviousValue,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.UserStability:
        case StabilityMetricType.MergedUserStability:
            return stabilityMetricGroupKey(
                StabilityMetricType.UserStability,
                monitoringStatistic.integrationInstallationId,
            );
        case StabilityMetricType.AdoptionRate:
            return stabilityMetricGroupKey(
                StabilityMetricType.AdoptionRate,
                monitoringStatistic.integrationInstallationId,
            );
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return UserRatingsMetricType.AvgUserRating;
    }
};

const obaaMonitoringStatGroupKey = (...components: (string | undefined)[]) => {
    return `#product-analytics-metric#${components.map((c) => c ?? 'unknown').join('#')}`;
};

export const displayMonitoringStatisticUnitLabel = (monitoringStatistic: MonitoringStatistic) => {
    switch (monitoringStatistic.type) {
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUser:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerActiveUserDeltaFromPreviousValue:
            return 'per DAU';
        case ObservabilityAndAnalyticsMetricType.EventsPerSession:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.EventsPerSessionDeltaFromPreviousValue:
            return 'per session';
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValues:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.AveragePropertyValuesDeltaFromPreviousPercentage:
            return 'avg value';
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValues:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.MedianPropertyValuesDeltaFromPreviousValue:
            return 'median value';
        case ObservabilityAndAnalyticsMetricType.P90PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P90PropertyValuesDeltaFromPreviousValue:
            return '90p';
        case ObservabilityAndAnalyticsMetricType.P95PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P95PropertyValuesDeltaFromPreviousValue:
            return '95p';
        case ObservabilityAndAnalyticsMetricType.P99PropertyValues:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.P99PropertyValuesDeltaFromPreviousValue:
            return '99p';
        case StabilityMetricType.UserStability:
        case StabilityMetricType.MergedUserStability:
        case StabilityMetricType.UserStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.UserStabilityDeltaFromPreviousValue:
            return 'users';
        case StabilityMetricType.SessionStability:
        case StabilityMetricType.MergedSessionStability:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousPercentage:
        case StabilityMetricType.SessionStabilityDeltaFromPreviousValue:
        case StabilityMetricType.AdoptionRate:
            return 'sessions';
        case UserRatingsMetricType.AvgUserRating:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousPercentage:
        case UserRatingsMetricType.AvgUserRatingDeltaFromPreviousValue:
            return 'average';
        case ObservabilityAndAnalyticsMetricType.GenericObaaValue:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.GenericObaaValueDeltaFromPreviousValue:
        case ObservabilityAndAnalyticsMetricType.Composed:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousPercentage:
        case ObservabilityAndAnalyticsMetricType.ComposedDeltaFromPreviousValue:
            return 'latest value';
    }
};

export interface InputFieldDataWithValidation extends Partial<InputNumberProps> {
    descriptionText?: string;
    validationRules?: Rule[];
}

// Org Overview

export interface AppSetAppData {
    id: string;
    imageUrl: string;
    platform: AppPlatform;
}

// an AppSet is a set of app ids whose stats can be combined and displayed in the org overview report. They can include app ids from different apps and platforms. Not to be confused with `AppGroup` which contains app ids of the same app in all the different platforms. This should be a simple data structure without only the calculated values required to display the tabs. The calculated values should be calculated by the parent component (using the right set of app ids) and passed to any child components that need them.
export interface AppSet {
    id: string;
    name: string;
    statsRequestId: string;
    apps: AppSetAppData[];
    icon?: typeof FieldBinaryOutlined;
}

export enum FeatureSection {
    HistoryTimeline = 'historyTimeline',
    AutomationTab = 'automationTab',
    ChecklistTab = 'checklistTab',
    ChecklistItems = 'checklistItems',
    KickoffReleasePilot = 'kickoffReleasePilot',
    KickoffReleaseDescription = 'kickoffReleaseDescription',
    KickoffFeatureAffiliation = 'kickoffFeatureAffiliation',
    KickoffReleaseBranch = 'kickoffReleaseBranch',
    KickoffVersionInCode = 'kickoffVersionInCode',
    KickoffPreviousReleaseTag = 'kickoffPreviousReleaseTag',
    KickoffCTAs = 'kickoffCTAs',

    FeatureReadinessWorkItems = 'featureReadinessWorkItems',

    TranslationStrings = 'translationStrings',
    TranslationCTAs = 'translationCTAs',
    ReleaseCandidateActiveBuild = 'releaseCandidateActiveBuild',
    ReleaseCandidateBuildHistory = 'releaseCandidateBuildHistory',

    RegressionTestingStatusPicker = 'regressionTestingStatusPicker',
    RegressionTestingTestItems = 'regressionTestingTestItems',

    BetaTestingActiveBuild = 'betaTestingActiveBuild',
    BetaTestingBetaSoak = 'betaTestingBetaSoak',
    BetaTestingWhatToTest = 'betaTestingWhatToTest',
    BetaTestingBetaGroups = 'betaTestingBetaGroups',
    BetaTestingStabilityModule = 'betaTestingStabilityModule',
    BetaTestingBetaBuildHistory = 'betaTestingBetaBuildHistory',

    ScreenshotsTabsContent = 'screenshotsTabsContent',

    MetadataReleaseNotesContent = 'metadataReleaseNotesContent',
    MetadataKeywordsOrShortDescription = 'metadataKeywordsOrShortDescription',
    MetadataAppName = 'metadataAppName',
    MetadataPromotionalText = 'metadataPromotionalText',
    MetadataDescription = 'metadataIOSDescription',
    MetadataSubtitle = 'metadataSubtitle',

    ApprovalsItemList = 'approvalsItemList',

    SubmissionSelectedBuildModule = 'submissionSelectedBuildModule',
    SubmissionAppStoreReleaseSettings = 'submissionAppStoreReleaseSettings',

    ReviewAppStoreReview = 'reviewAppStoreReview',

    ReleaseReleasedBuild = 'releaseReleasedBuild',
    ReleasePhasedReleaseInfo = 'releasePhasedReleaseInfo',
    ReleaseAutomatedReleaseTasks = 'releaseAutomatedReleaseTasks',

    OverviewTopOverview = 'overviewTopOverview',
    OverviewProduct = 'overviewProduct',
    OverviewCode = 'overviewCode',

    RolloutTopSummary = 'rolloutTopSummary',
    RolloutPhasedReleaseModule = 'rolloutPhasedReleaseModule',
    RolloutStabilityModule = 'rolloutStabilityModule',
    RolloutObservabilityAndAnalyticsModule = 'rolloutObservabilityAndAnalyticsModule',
    RolloutUserReviewsModule = 'rolloutUserReviewsModule',

    FeatureFlagsTable = 'featureFlagsTable',

    // Org Overview
    OrgOverviewWorkMetrics = 'orgOverviewWorkMetrics',
    OrgOverviewBuildMetrics = 'orgOverviewBuildMetrics',
    OrgOverviewStabilityHealthMetrics = 'orgOverviewStabilityHealthMetrics',
    OrgOverviewMobileDevOpsMetrics = 'orgOverviewMobileDevOpsMetrics',
}

export interface BarGraphTimeBucketDataPoint {
    type: string;
    timeBucket: string;
    value: number;
    total: number;
    axisLabel?: string;
    tooltipTitle?: string;
}

export interface AppStoreArtifactUploadWorkflowOption {
    id: WorkflowType;
    workflowDescription: string;
    workflowName: string;
}

export interface AppStoreArtifactUploadDestinationTypeOption {
    id: IntegrationInstallationId;
    type: AppStoreArtifactUploadDestinationType;
    name: string;
    alias?: string;
    identifier: string;
    availableTracks?: string[];
}

export interface DsymsUploadWorkflowOption {
    id: WorkflowType;
    workflowDescription: string;
    workflowName: string;
}

export const CALLER_CONTEXT_PARAM = 'callerContext';
export const REDIRECT_URL_PARAM = 'redirectUrl';
export const CAPABILITY_URL_PARAM = 'capabilityUrl';

export enum SetupStepsCallerContext {
    BuildDistroConnectCICDIntegration = 'BuildDistroConnectCICDIntegration',
    CommonIntegrationCapability = 'CommonIntegrationCapability',
}

export interface WorkflowAndBranchData {
    workflowName: string;
    branch: string;
    vcsUsers: VCSUserData[];
    type: BuildDistroBucketRuleType;
}

export enum AndroidStagedRolloutStyle {
    NotStaged = 'notStaged',
    Staged = 'staged',
}

// This is almost the same as using `?` but this actually requires defining the property, it cannot be omitted even if it's null/undefined
export type Nullable<T> = T | undefined | null;

export const doesPercentageMatchDayProgression = (
    percentage: number,
    dayNumber: number,
    dayPairs: [number, number][] | null,
): boolean => {
    if (percentage < 100 || !dayPairs) {
        return false;
    }

    const calculatedPercentage = dayPairs[dayNumber - 1]?.[1];
    return calculatedPercentage === percentage;
};
